import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SaveBtn } from "@/admin/AdminLayout";
import { VersionHistory } from "@/admin/VersionHistory";

const fmt = (d: string) => new Date(d).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

function useStamp(res: string, id?: string, tick = 0) {
  const [st, setSt] = useState<any>(null);
  const load = useCallback(() => { if (id) api.get(`/admin-x/save-stamp/${res}/${id}`).then((r) => setSt(r.data)).catch(() => setSt(null)); }, [res, id]);
  useEffect(() => { load(); }, [load, tick]);
  return { st, load };
}

type P = { res: string; id?: string; onSave: () => Promise<any>; value?: any; testId: string; label?: string; className?: string; onRestored?: () => Promise<any> | void };

// Save button with server-recorded "Last saved … by …" and a History button, like Site, Menus & Home.
export function RecordedSave({ res, id, onSave, value, testId, label = "Save", className = "", onRestored }: P) {
  const { st, load } = useStamp(res, id);
  const [ver, setVer] = useState(0);
  const save = async () => { const ok = await onSave(); if (ok !== false) load(); return ok; };
  return (
    <div className={`inline-flex items-start gap-2 ${className}`} data-testid={`${testId}-recorded`}>
      {st?.history && onRestored && <VersionHistory res={res} itemId={id} onRestored={async () => { await onRestored(); setVer((v) => v + 1); load(); }} />}
      <SaveBtn key={`${id || "new"}-${ver}`} onSave={save} value={value} savedAt={st?.saved_at} savedBy={st?.saved_by} label={label} testId={testId} />
    </div>
  );
}

// For screens that save on each toggle: shows the last-saved stamp and History.
export function RecordedStamp({ res, id, tick = 0, onRestored, testId }: { res: string; id: string; tick?: number; onRestored?: () => Promise<any> | void; testId: string }) {
  const { st, load } = useStamp(res, id, tick);
  return (
    <div className="inline-flex flex-col items-end gap-1" data-testid={testId}>
      {st?.history && onRestored && <VersionHistory res={res} itemId={id} onRestored={async () => { await onRestored(); load(); }} />}
      {st?.saved_at && <span className="text-[10px] text-neutral-500 tracking-wide" data-testid={`${testId}-text`}>Last saved {fmt(st.saved_at)}{st.saved_by && <> by <span className="text-neutral-800">{st.saved_by}</span></>}</span>}
    </div>
  );
}

// Small "Updated … by …" line for inbox rows that save straight away.
export const SavedBy = ({ d, testId }: { d: any; testId: string }) =>
  d?.saved_at ? <p className="text-[10px] text-neutral-500 mt-1 whitespace-nowrap" data-testid={testId}>Updated {fmt(d.saved_at)}{d.saved_by && <> by {d.saved_by}</>}</p> : null;
